import {
  dronesAdapter,
  fleetsAdapter,
  flyRoutesAdapter,
  flySectionsAdapter,
  flyZonesAdapter,
  pilotsAdapter,
  strategicDronesAdapter,
} from '@redux/utm/utm.adapters';
import { droneSchema, fleetSchema, flyRouteSchema, flySectionSchema, utmSchema } from '@redux/utm/utm.schema';
import { createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit';
import { normalizeOne } from '@utils/schema.utils';
import { normalize } from 'normalizr';

import { Drone, NormalizedDrone, StrategicDrone } from '@/types/utm/drone.types';
import { Fleet, NormalizedFleet } from '@/types/utm/fleet.type';
import { FlyZone } from '@/types/utm/flyZone.types';
import { Pilot } from '@/types/utm/pilot.types';
import { FlyRoute, FlySection, NormalizedFlyRoute, NormalizedFlySection } from '@/types/utm/route.types';

import { userLogout } from '../authent/authent.reducer';

export interface UtmState {
  pilots: EntityState<Pilot, string>;
  drones: EntityState<NormalizedDrone, string>;
  fleets: EntityState<NormalizedFleet, string>;
  strategicDrones: EntityState<StrategicDrone, number>;
  flyZones: EntityState<FlyZone, string>;
  flySections: EntityState<NormalizedFlySection, string>;
  flyRoutes: EntityState<NormalizedFlyRoute, string>;
}

export const UTM_INITIAL_STATE: UtmState = {
  pilots: pilotsAdapter.getInitialState(),
  drones: dronesAdapter.getInitialState(),
  fleets: fleetsAdapter.getInitialState(),
  strategicDrones: strategicDronesAdapter.getInitialState(),
  flyZones: flyZonesAdapter.getInitialState(),
  flySections: flySectionsAdapter.getInitialState(),
  flyRoutes: flyRoutesAdapter.getInitialState(),
};

const utmReducers = createSlice({
  name: 'utm',
  initialState: UTM_INITIAL_STATE,
  reducers: {
    updateUtm: (
      state,
      action: PayloadAction<{
        drones: Drone[];
        strategicDrones: StrategicDrone[];
        pilots: Pilot[];
        fleets: Fleet[];
        flyRoutes: FlyRoute[];
        flySections: FlySection[];
        flyZones: FlyZone[];
      }>,
    ) => {
      const { pilots, strategicDrones, flyZones } = action.payload;
      const { drones, fleets, flySections, flyRoutes } = normalize(action.payload, utmSchema).entities as unknown as {
        drones?: NormalizedDrone[];
        fleets?: NormalizedFleet[];
        flyRoutes?: NormalizedFlyRoute[];
        flySections?: NormalizedFlySection[];
      };

      pilotsAdapter.setAll(state.pilots, pilots);
      strategicDronesAdapter.setAll(state.strategicDrones, strategicDrones);
      dronesAdapter.setAll(state.drones, drones ?? []);
      fleetsAdapter.setAll(state.fleets, fleets ?? []);
      flyZonesAdapter.setAll(state.flyZones, flyZones);
      flySectionsAdapter.setAll(state.flySections, flySections ?? []);
      flyRoutesAdapter.setAll(state.flyRoutes, flyRoutes ?? []);
    },
    addPilot: ({ pilots }, action: PayloadAction<Pilot>) => {
      pilotsAdapter.addOne(pilots, action.payload);
    },
    updatePilot: ({ pilots }, action: PayloadAction<Pilot>) => {
      pilotsAdapter.upsertOne(pilots, action.payload);
    },
    deletePilot: ({ pilots }, action: PayloadAction<Pilot>) => {
      pilotsAdapter.removeOne(pilots, action.payload.code);
    },
    addStrategicDrone: ({ strategicDrones }, action: PayloadAction<StrategicDrone>) => {
      strategicDronesAdapter.addOne(strategicDrones, action.payload);
    },
    updateStrategicDrone: ({ strategicDrones }, action: PayloadAction<StrategicDrone>) => {
      strategicDronesAdapter.upsertOne(strategicDrones, action.payload);
    },
    deleteStrategicDrone: ({ strategicDrones }, action: PayloadAction<StrategicDrone>) => {
      strategicDronesAdapter.removeOne(strategicDrones, action.payload.id);
    },
    addDrone: ({ drones }, action: PayloadAction<Drone>) => {
      const normalizedDrone = normalizeOne(action.payload, droneSchema);
      normalizedDrone && dronesAdapter.addOne(drones, normalizedDrone as NormalizedDrone);
    },
    updateDrone: ({ drones }, action: PayloadAction<Drone>) => {
      const normalizedDrone = normalizeOne(action.payload, droneSchema);
      normalizedDrone && dronesAdapter.upsertOne(drones, normalizedDrone as NormalizedDrone);
    },
    deleteDrone: ({ drones }, action: PayloadAction<Drone>) => {
      dronesAdapter.removeOne(drones, action.payload.code);
    },
    addFleet: ({ fleets }, action: PayloadAction<Fleet>) => {
      const normalizedFleet = normalizeOne(action.payload, fleetSchema);
      normalizedFleet && fleetsAdapter.addOne(fleets, normalizedFleet as NormalizedFleet);
    },
    updateFleet: ({ fleets }, action: PayloadAction<Fleet>) => {
      const normalizedFleet = normalizeOne(action.payload, fleetSchema);
      normalizedFleet && fleetsAdapter.upsertOne(fleets, normalizedFleet as NormalizedFleet);
    },
    deleteFleet: ({ fleets }, action: PayloadAction<Fleet>) => {
      fleetsAdapter.removeOne(fleets, action.payload.code);
    },
    addFlyZone: ({ flyZones }, action: PayloadAction<FlyZone>) => {
      flyZonesAdapter.addOne(flyZones, action.payload);
    },
    updateFlyZone: ({ flyZones }, action: PayloadAction<FlyZone>) => {
      flyZonesAdapter.upsertOne(flyZones, action.payload);
    },
    deleteFlyZone: ({ flyZones }, action: PayloadAction<FlyZone>) => {
      flyZonesAdapter.removeOne(flyZones, action.payload.code);
    },
    addFlySection: ({ flySections }, action: PayloadAction<FlySection>) => {
      const normalizedFlySection = normalizeOne(action.payload, flySectionSchema);
      normalizedFlySection && flySectionsAdapter.addOne(flySections, normalizedFlySection as NormalizedFlySection);
    },
    updateFlySection: ({ flySections }, action: PayloadAction<FlySection>) => {
      const normalizedFlySection = normalizeOne(action.payload, flySectionSchema);
      normalizedFlySection && flySectionsAdapter.upsertOne(flySections, normalizedFlySection as NormalizedFlySection);
    },
    deleteFlySection: ({ flySections }, action: PayloadAction<FlySection>) => {
      flySectionsAdapter.removeOne(flySections, action.payload.code);
    },
    addFlyRoute: ({ flyRoutes }, action: PayloadAction<FlyRoute>) => {
      const normalizedFlyRoute = normalizeOne(action.payload, flyRouteSchema);
      normalizedFlyRoute && flyRoutesAdapter.addOne(flyRoutes, normalizedFlyRoute as NormalizedFlyRoute);
    },
    updateFlyRoute: ({ flyRoutes }, action: PayloadAction<FlyRoute>) => {
      const normalizedFlyRoute = normalizeOne(action.payload, flyRouteSchema);
      normalizedFlyRoute && flyRoutesAdapter.upsertOne(flyRoutes, normalizedFlyRoute as NormalizedFlyRoute);
    },
    deleteFlyRoute: ({ flyRoutes }, action: PayloadAction<FlyRoute>) => {
      flyRoutesAdapter.removeOne(flyRoutes, action.payload.code);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userLogout, () => UTM_INITIAL_STATE);
  },
});
export const {
  updateUtm,
  addPilot,
  updatePilot,
  deletePilot,
  addDrone,
  updateDrone,
  deleteDrone,
  addFleet,
  deleteFleet,
  updateFleet,
  addStrategicDrone,
  updateStrategicDrone,
  deleteStrategicDrone,
  addFlyZone,
  updateFlyZone,
  deleteFlyZone,
  addFlySection,
  updateFlySection,
  deleteFlySection,
  addFlyRoute,
  updateFlyRoute,
  deleteFlyRoute,
} = utmReducers.actions;

export type UtmActionType = typeof utmReducers.actions;
export default utmReducers.reducer;
